import React, { useState, FormEvent } from 'react';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { messages } from '../constants/messages';
import { useContent } from '../ContentContext';
import { SEND_EMAIL_URL } from '../config';
import { showToast } from './Toast';

const Contact = () => {
  const { content } = useContent();
  const cp = (content as any).contact_page || messages.contact;
  const common = content.common;

  const bgColor     = cp.bg_color      || '#ffffff';
  const formBg      = cp.form_bg_color || '#f9fafb';
  const accent      = cp.accent_color  || '#EF5D22';
  const titleSize   = cp.title_size    || 'text-5xl';

  const formFields: any[] = (content as any).contact_form_fields || [];

  // Form state
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await fetch(SEND_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          form_type: 'Contact Form',
          source: 'Contact Page'
        })
      });

      const json = await res.json();

      if (json.status === 'success') {
        setSubmitStatus({ type: 'success', message: json.message });
        showToast('🎉 Thank you! Your message has been sent successfully. We will get back to you soon.', 'success');
        setFormData({}); // Reset form
        // Reset form inputs
        const form = e.target as HTMLFormElement;
        form.reset();
      } else {
        setSubmitStatus({ type: 'error', message: json.message || 'Failed to send message. Please try again.' });
        showToast(json.message || 'Failed to send message. Please try again.', 'error');
      }
    } catch (err) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please check your connection and try again.' });
      showToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render a single dynamic form field
  const renderField = (field: any) => {
    const baseClass = `w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition text-gray-900`;
    const wrapClass = field.col_span === 2 || field.col_span === '2' ? 'md:col-span-2' : '';
    const fieldKey = field.label.toLowerCase().replace(/[^a-z0-9]/g, '_');

    if (field.field_type === 'textarea') {
      return (
        <div key={field.id} className={`space-y-2 ${wrapClass}`}>
          <label className="text-sm font-bold text-gray-700 ml-1">{field.label}{field.is_required ? ' *' : ''}</label>
          <textarea
            rows={4}
            placeholder={field.placeholder || ''}
            required={!!field.is_required}
            className={`${baseClass} resize-none`}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
          />
        </div>
      );
    }

    if (field.field_type === 'select') {
      const opts: string[] = field.options ? field.options.split('|').map((o: string) => o.trim()).filter(Boolean) : [];
      return (
        <div key={field.id} className={`space-y-2 ${wrapClass}`}>
          <label className="text-sm font-bold text-gray-700 ml-1">{field.label}{field.is_required ? ' *' : ''}</label>
          <select 
            required={!!field.is_required} 
            className={baseClass}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
          >
            <option value="">— Select —</option>
            {opts.map((o, i) => <option key={i} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }

    return (
      <div key={field.id} className={`space-y-2 ${wrapClass}`}>
        <label className="text-sm font-bold text-gray-700 ml-1">{field.label}{field.is_required ? ' *' : ''}</label>
        <input
          type={field.field_type || 'text'}
          placeholder={field.placeholder || ''}
          required={!!field.is_required}
          className={baseClass}
          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
        />
      </div>
    );
  };

  // Fallback static fields when no DB fields exist
  const staticFields = () => (
    <>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">{messages.contact.formLabels.fullName}</label>
          <input 
            type="text" 
            placeholder={messages.contact.formPlaceholders.fullName} 
            className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition text-gray-900" 
            onChange={(e) => handleInputChange('full_name', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">{messages.contact.formLabels.phone}</label>
          <input 
            type="tel" 
            placeholder={messages.contact.formPlaceholders.phone} 
            className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition text-gray-900" 
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-700 ml-1">{messages.contact.formLabels.email}</label>
        <input 
          type="email" 
          placeholder={messages.contact.formPlaceholders.email} 
          className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition text-gray-900" 
          onChange={(e) => handleInputChange('email', e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-700 ml-1">{messages.contact.formLabels.time}</label>
        <select 
          className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition text-gray-900"
          onChange={(e) => handleInputChange('preferred_time', e.target.value)}
        >
          {messages.contact.timeOptions.map((o, i) => <option key={i} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-700 ml-1">{messages.contact.formLabels.message}</label>
        <textarea 
          rows={4} 
          placeholder={messages.contact.formPlaceholders.message} 
          className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition text-gray-900 resize-none" 
          onChange={(e) => handleInputChange('message', e.target.value)}
        />
      </div>
    </>
  );

  return (
    <section id="contact" className="py-12 sm:py-24 px-4 sm:px-10" style={{ backgroundColor: bgColor }}>
      <div className="container mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        {/* Left column — info */}
        <div>
          <span className="font-bold tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4 block" style={{ color: accent }}>
            {cp.badge || messages.contact.badge}
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:${titleSize} font-extrabold mb-6 sm:mb-8 text-gray-900 leading-tight`}>
            {cp.titlePart1 || messages.contact.titlePart1}{' '}
            <span style={{ color: accent }}>{cp.titlePart2 || messages.contact.titlePart2}</span>{' '}
            {cp.titlePart3 || messages.contact.titlePart3}
          </h2>
          {(cp.description || messages.contact.description) && (
            <div
              className="text-gray-600 text-base sm:text-lg mb-8 sm:mb-12 max-w-lg leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: cp.description || messages.contact.description }}
            />
          )}

          <div className="space-y-6 sm:space-y-8">
            <div className="flex items-start space-x-4 sm:space-x-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shrink-0 border"
                style={{ backgroundColor: accent + '1a', color: accent, borderColor: accent + '1a' }}>
                <MapPinIcon className="w-7 h-7" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">{cp.officeTitle || messages.contact.officeTitle}</h4>
                <p className="text-gray-600">{common.fullAddress || messages.common.fullAddress}</p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 border"
                style={{ backgroundColor: '#0066CC1a', color: '#0066CC', borderColor: '#0066CC1a' }}>
                <PhoneIcon className="w-7 h-7" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">{cp.callTitle || messages.contact.callTitle}</h4>
                <p className="text-gray-600 font-bold text-lg">{common.phone || messages.common.phone}</p>
                <p className="text-gray-400 text-sm">{cp.callHours || messages.contact.callHours}</p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-700 shrink-0 border border-gray-200">
                <EnvelopeIcon className="w-7 h-7" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">{cp.emailTitle || messages.contact.emailTitle}</h4>
                <p className="text-gray-600 font-medium">{common.email || messages.common.email}</p>
                <p className="text-gray-400 text-sm">{cp.emailResponse || messages.contact.emailResponse}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right column — form */}
        <div className="p-10 rounded-[2.5rem] border border-gray-100 shadow-xl" style={{ backgroundColor: formBg }}>
          {/* Success/Error Messages */}
          {submitStatus && (
            <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {submitStatus.type === 'success' 
                ? <CheckCircleIcon className="w-6 h-6 text-green-600 shrink-0" />
                : <ExclamationCircleIcon className="w-6 h-6 text-red-600 shrink-0" />
              }
              <p className="text-sm font-medium">{submitStatus.message}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {formFields.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formFields.map(renderField)}
              </div>
            ) : staticFields()}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full text-white py-5 rounded-xl font-bold text-xl shadow-xl transition duration-300 transform hover:-translate-y-1 ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              style={{ backgroundColor: accent }}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (cp.ctaButton || messages.contact.ctaButton)}
            </button>
          </form>
        </div>
      </div>

      {/* Google Map — full width below the two-column grid */}
      {cp.map_embed && cp.show_map !== '0' && (
        <div className="container mx-auto mt-16">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
            <iframe
              src={cp.map_embed}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              title="Location Map"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Contact;
